/* kit-product.js — <kit-product> custom element for sections/kit-main-product.liquid */
(function () {
  if (customElements.get('kit-product')) return;

  const money = (cents) => (window.Kit ? window.Kit.formatMoney(cents) : '$' + (cents / 100).toFixed(2));

  class KitProduct extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-kit-product-json]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.variant = this.data.variants.find((v) => v.id === this.data.selectedVariantId) || this.data.variants[0];
      this.offer = this.querySelector('[data-kit-offer]');
      this.cards = this.offer ? Array.from(this.offer.querySelectorAll('[data-kit-tier]')) : [];
      this.maxQty = this.cards.reduce((m, c) => Math.max(m, Number(c.dataset.qty || 1)), 1);
      this.units = new Array(this.maxQty).fill(this.variant.id);
      this.touched = new Set(); // item positions the shopper picked a variant for themselves
      // Quantity breaks: pick options per item inside the selected card (the picker above is not rendered).
      this.unitMode = !!this.offer && this.offer.dataset.kitOffer === 'quantity_breaks' &&
        this.offer.dataset.units !== 'above' && this.data.variants.length > 1;
      this.atc = this.querySelector('[data-kit-atc]');
      this.buyNow = this.querySelector('[data-kit-buy-now]');
      this.errorEl = this.querySelector('[data-kit-error]');

      this.initGallery();
      this.initOptions();
      this.initOffer();
      this.initQty();
      this.initButtons();
      this.initSticky();
      this.initDelivery();
      this.render();
    }

    /* ---------------- Gallery ---------------- */
    initGallery() {
      this.track = this.querySelector('[data-kit-track]');
      this.slides = this.track ? Array.from(this.track.children) : [];
      this.thumbs = Array.from(this.querySelectorAll('[data-kit-thumb]'));
      this.counter = this.querySelector('[data-kit-counter]');
      this.mediaIndex = 0;
      if (!this.track || this.slides.length < 2) return;

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goToSlide(Number(t.dataset.kitThumb))));
      const prev = this.querySelector('[data-kit-prev]');
      const next = this.querySelector('[data-kit-next]');
      if (prev) prev.addEventListener('click', () => this.goToSlide((this.mediaIndex - 1 + this.slides.length) % this.slides.length));
      if (next) next.addEventListener('click', () => this.goToSlide((this.mediaIndex + 1) % this.slides.length));

      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.mediaIndex) this.setActiveSlide(i);
        });
      }, { passive: true });
    }

    goToSlide(i, instant) {
      if (!this.track || !this.slides[i]) return;
      this.track.scrollTo({ left: this.slides[i].offsetLeft, behavior: instant ? 'auto' : 'smooth' });
      this.setActiveSlide(i);
    }

    setActiveSlide(i) {
      this.mediaIndex = Math.max(0, Math.min(i, this.slides.length - 1));
      this.thumbs.forEach((t, k) => t.classList.toggle('is-active', k === this.mediaIndex));
      if (this.counter) this.counter.textContent = `${this.mediaIndex + 1} / ${this.slides.length}`;
      const thumb = this.thumbs[this.mediaIndex];
      if (thumb && thumb.parentElement) {
        const p = thumb.parentElement;
        const horizontal = p.scrollWidth > p.clientWidth;
        if (horizontal) p.scrollTo({ left: thumb.offsetLeft - p.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
      }
    }

    goToMedia(mediaId) {
      if (!mediaId) return;
      const i = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      if (i >= 0) this.goToSlide(i);
    }

    /* ---------------- Options ---------------- */
    initOptions() {
      this.optionSets = Array.from(this.querySelectorAll('[data-kit-option]'));
      this.optionSets.forEach((set) => {
        set.addEventListener('change', () => this.onOptionChange());
      });
    }

    selectedOptions() {
      return this.optionSets.map((set) => {
        const checked = set.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const opts = this.selectedOptions();
      const match = this.data.variants.find((v) => v.options.every((o, i) => o === opts[i]));
      this.optionSets.forEach((set, i) => {
        const label = set.querySelector('[data-kit-option-value]');
        if (label) label.textContent = opts[i] || '';
      });
      if (!match) {
        this.variant = null;
        this.render();
        return;
      }
      this.setVariant(match, true);
    }

    setVariant(variant, fromPicker) {
      this.variant = variant;
      this.units = this.units.map((id, k) => (this.touched.has(k) ? id : variant.id));
      if (fromPicker) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        this.goToMedia(variant.mediaId);
      }
      this.updateAvailability();
      this.render();
    }

    updateAvailability() {
      // Mark option values that don't combine into an available variant with the other selections.
      const opts = this.selectedOptions();
      this.optionSets.forEach((set, i) => {
        set.querySelectorAll('input').forEach((input) => {
          const test = opts.slice();
          test[i] = input.value;
          const ok = this.data.variants.some((v) => v.available && v.options.every((o, k) => o === test[k]));
          const label = set.querySelector(`label[for="${input.id}"]`);
          if (label) label.classList.toggle('is-unavailable', !ok);
        });
      });
    }

    /* ---------------- Offer ---------------- */
    initOffer() {
      if (!this.offer) return;
      this.offerType = this.offer.dataset.kitOffer;
      this.cards.forEach((card, i) => {
        card.addEventListener('click', (e) => {
          if (e.target.closest('[data-kit-units]')) return;
          this.selectCard(i);
        });
        card.addEventListener('keydown', (e) => {
          if (e.target !== card) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.selectCard(i);
          } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            e.preventDefault();
            const n = (i + 1) % this.cards.length;
            this.selectCard(n);
            this.cards[n].focus();
          } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const n = (i - 1 + this.cards.length) % this.cards.length;
            this.selectCard(n);
            this.cards[n].focus();
          }
        });
      });
      this.selected = Math.max(0, this.cards.findIndex((c) => c.classList.contains('is-selected')));
      // close per-item dropdowns when tapping anywhere else
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.kit-dd')) this.closeDropdowns();
      });
    }

    selectCard(i) {
      this.selected = i;
      this.cards.forEach((c, k) => {
        c.classList.toggle('is-selected', k === i);
        c.setAttribute('aria-checked', k === i ? 'true' : 'false');
      });
      this.render();
    }

    variantById(id) {
      return this.data.variants.find((v) => v.id === Number(id));
    }

    cardTotals(card, isSelected) {
      const v = this.variant;
      if (!v) return { total: 0, compare: 0 };
      const qty = Number(card.dataset.qty || 1);
      const pct = Number(card.dataset.pct || 0);
      const cmp = (x) => Math.max(x.compare || 0, x.price);

      if (this.offerType === 'bundle') {
        if (card.hasAttribute('data-bundle')) {
          const bp = Number(this.offer.dataset.bundlePrice);
          const bc = Number(this.offer.dataset.bundleCompare);
          const off = Number(this.offer.dataset.amountOff || 0);
          return { total: Math.max(0, v.price + bp - off), compare: cmp(v) + bc, separately: v.price + bp };
        }
        return { total: v.price, compare: cmp(v) };
      }

      const lineVariants = isSelected && this.data.variants.length > 1
        ? this.units.slice(0, qty).map((id) => this.variantById(id) || v)
        : new Array(qty).fill(v);
      const gross = lineVariants.reduce((s, x) => s + x.price, 0);
      const compare = lineVariants.reduce((s, x) => s + cmp(x), 0);
      const amount = Number(card.dataset.amount || 0); // fixed amount off (cents), overrides pct
      const total = amount > 0 ? Math.max(0, gross - amount) : Math.round((gross * (100 - pct)) / 100);
      return { total, compare, each: Math.floor(total / qty) }; // floor matches Liquid's integer divided_by
    }

    renderUnits(card, isSelected) {
      const box = card.querySelector('[data-kit-units]');
      if (!box) return;
      const qty = Number(card.dataset.qty || 1);
      const show = isSelected && this.data.variants.length > 1 && (this.unitMode || qty > 1);
      box.hidden = !show;
      if (!show) {
        box.innerHTML = '';
        return;
      }
      if (this.unitMode) {
        this.renderUnitOptions(box, qty);
        return;
      }
      const swatches = this.data.options.length === 1 ? this.data.swatches[0] || {} : {};
      const useDots = this.data.options.length === 1 && this.data.variants.every((x) => swatches[x.options[0]]);
      box.innerHTML = '';
      for (let u = 0; u < qty; u++) {
        const current = this.variantById(this.units[u]) || this.variant;
        const row = document.createElement('div');
        row.className = 'kit-offer__unit';
        const label = document.createElement('span');
        label.className = 'kit-muted';
        label.textContent = `#${u + 1} · ${current.title}`;
        row.appendChild(label);
        if (useDots) {
          const dots = document.createElement('span');
          dots.className = 'kit-offer__dots';
          this.data.variants.forEach((x) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'kit-offer__dot' + (x.id === current.id ? ' is-active' : '');
            dot.style.background = swatches[x.options[0]];
            dot.title = x.title;
            dot.disabled = !x.available;
            dot.setAttribute('aria-label', `Item ${u + 1}: ${x.title}`);
            dot.setAttribute('data-kit-unit-dot', '');
            dot.addEventListener('click', (e) => {
              e.stopPropagation();
              this.units[u] = x.id;
              this.render();
            });
            dots.appendChild(dot);
          });
          row.appendChild(dots);
        } else {
          const select = document.createElement('select');
          select.className = 'kit-offer__select';
          select.setAttribute('aria-label', `Item ${u + 1}`);
          this.data.variants.forEach((x) => {
            const o = document.createElement('option');
            o.value = x.id;
            o.textContent = x.title + (x.available ? '' : ' (sold out)');
            o.disabled = !x.available;
            if (x.id === current.id) o.selected = true;
            select.appendChild(o);
          });
          select.addEventListener('click', (e) => e.stopPropagation());
          select.addEventListener('change', (e) => {
            this.units[u] = Number(e.target.value);
            this.touched.add(u);
            this.render();
          });
          row.appendChild(select);
        }
        box.appendChild(row);
      }
    }

    // One dropdown per product option; values with a swatch show a color dot.
    renderUnitOptions(box, qty) {
      box.innerHTML = '';
      box.classList.add('kit-offer__units--opts');
      const hint = document.createElement('div');
      hint.className = 'kit-offer__units-hint kit-muted';
      const choosable = this.data.options.map((name, i) => i).filter((i) => ((this.data.optionValues && this.data.optionValues[i]) || []).length > 1);
      hint.textContent = `Select ${choosable.map((i) => this.data.options[i]).join(' & ').toLowerCase()}${qty > 1 ? ' for each item' : ''}`;
      box.appendChild(hint);
      for (let u = 0; u < qty; u++) {
        const current = this.variantById(this.units[u]) || this.variant;
        const row = document.createElement('div');
        row.className = 'kit-offer__unit kit-offer__unit--opts';
        if (qty > 1) {
          const label = document.createElement('span');
          label.className = 'kit-offer__unit-label';
          label.textContent = `Item ${u + 1}`;
          row.appendChild(label);
        }
        const controls = document.createElement('div');
        controls.className = 'kit-offer__unit-controls';
        choosable.forEach((i) => controls.appendChild(this.unitOptionControl(u, i, current)));
        row.appendChild(controls);
        box.appendChild(row);
      }
    }

    unitOptionControl(u, i, current) {
      const name = this.data.options[i];
      const values = (this.data.optionValues && this.data.optionValues[i]) || [];
      const swatches = this.data.swatches[i] || {};
      const availableWith = (val) => this.data.variants.some((x) => x.available &&
        x.options.every((o, k) => (k === i ? o === val : o === current.options[k])));

      // Custom dropdown (a native <select> can't show color dots in its list).
      const key = `${u}-${i}`;
      const withDot = (el, val) => {
        if (!swatches[val]) return;
        if (swatches[val] === 'image') {
          const photo = (this.data.variants.find((x) => x.options[i] === val && x.image) || {}).image;
          if (!photo) return;
          const thumb = document.createElement('img');
          thumb.className = 'kit-dd__thumb';
          thumb.src = photo;
          thumb.alt = '';
          thumb.width = 36;
          thumb.height = 36;
          el.appendChild(thumb);
          return;
        }
        const dot = document.createElement('span');
        dot.className = 'kit-dd__dot';
        dot.style.background = swatches[val];
        dot.setAttribute('aria-hidden', 'true');
        el.appendChild(dot);
      };
      const dd = document.createElement('div');
      dd.className = 'kit-dd' + (values.some((val) => swatches[val]) ? ' kit-dd--swatch' : '') + (values.some((val) => swatches[val] === 'image') ? ' kit-dd--photo' : '');
      dd.dataset.kitDd = key;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'kit-dd__btn';
      btn.setAttribute('aria-haspopup', 'listbox');
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-label', `Item ${u + 1} ${name}: ${current.options[i]}`);
      withDot(btn, current.options[i]);
      const text = document.createElement('span');
      text.className = 'kit-dd__text';
      text.textContent = current.options[i];
      btn.appendChild(text);
      btn.insertAdjacentHTML('beforeend', '<svg class="kit-dd__caret" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>');

      const list = document.createElement('ul');
      list.className = 'kit-dd__list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', `Item ${u + 1} ${name}`);
      list.hidden = true;
      values.forEach((val) => {
        const li = document.createElement('li');
        const selected = current.options[i] === val;
        li.className = 'kit-dd__opt' + (selected ? ' is-selected' : '') + (availableWith(val) ? '' : ' is-unavailable');
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', selected ? 'true' : 'false');
        li.tabIndex = -1;
        withDot(li, val);
        const t = document.createElement('span');
        t.className = 'kit-dd__text';
        t.textContent = val + (availableWith(val) ? '' : ' (sold out)');
        li.appendChild(t);
        li.addEventListener('click', (e) => {
          e.stopPropagation();
          this.pickDropdown(key, u, i, val);
        });
        list.appendChild(li);
      });

      const opts = () => Array.from(list.children);
      const open = (focusIndex) => {
        this.closeDropdowns(dd);
        list.hidden = false;
        dd.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
        const items = opts();
        const start = focusIndex ?? Math.max(0, items.findIndex((o) => o.classList.contains('is-selected')));
        items[start]?.focus({ preventScroll: true });
        list.scrollIntoView({ block: 'nearest' });
      };
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (list.hidden) open();
        else this.closeDropdowns();
      });
      btn.addEventListener('keydown', (e) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
          e.preventDefault();
          open();
        }
      });
      list.addEventListener('keydown', (e) => {
        const items = opts();
        const k = items.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const n = (k + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
          items[n].focus();
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (k >= 0) this.pickDropdown(key, u, i, values[k]);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          this.closeDropdowns();
          btn.focus();
        } else if (e.key === 'Tab') {
          this.closeDropdowns();
        }
      });
      dd.append(btn, list);
      return dd;
    }

    pickDropdown(key, u, i, value) {
      this.closeDropdowns();
      this.setUnitOption(u, i, value);
      // the units box is rebuilt on render: put focus back on the same dropdown
      this.querySelector(`[data-kit-dd="${key}"] .kit-dd__btn`)?.focus({ preventScroll: true });
    }

    closeDropdowns(except) {
      this.querySelectorAll('.kit-dd.is-open').forEach((dd) => {
        if (dd === except) return;
        dd.classList.remove('is-open');
        dd.querySelector('.kit-dd__list').hidden = true;
        dd.querySelector('.kit-dd__btn').setAttribute('aria-expanded', 'false');
      });
    }

    setUnitOption(u, i, value) {
      const current = this.variantById(this.units[u]) || this.variant;
      const wanted = current.options.slice();
      wanted[i] = value;
      let next = this.data.variants.find((x) => x.options.every((o, k) => o === wanted[k]));
      if (!next || !next.available) {
        // keep the value the shopper just picked, adjust the other options to an available variant
        next = this.data.variants.find((x) => x.available && x.options[i] === value) || next;
      }
      if (!next) return;
      this.units[u] = next.id;
      // each item is independent: changing item 1 never changes the other items
      this.touched.add(u);
      this.variant = next;
      if (u === 0) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', next.id);
        window.history.replaceState({}, '', url.toString());
      }
      this.goToMedia(next.mediaId);
      this.render();
    }

    renderOffer() {
      if (!this.offer || !this.cards.length) return null;
      let selectedTotals = null;
      this.cards.forEach((card, i) => {
        const isSel = i === this.selected;
        const t = this.cardTotals(card, isSel);
        const totalEl = card.querySelector('[data-kit-total]');
        const cmpEl = card.querySelector('[data-kit-compare]');
        if (totalEl) totalEl.textContent = money(t.total);
        if (cmpEl) {
          cmpEl.textContent = money(t.compare);
          cmpEl.hidden = !(t.compare > t.total);
        }
        const eachEl = card.querySelector('[data-kit-each]');
        if (eachEl && Number(card.dataset.qty) > 1 && t.each) eachEl.textContent = `${money(t.each)} each`;
        const sepEl = card.querySelector('[data-kit-separately]');
        if (sepEl && t.separately) sepEl.textContent = `${money(t.separately)} if bought separately`;
        this.renderUnits(card, isSel);
        if (isSel) selectedTotals = t;
      });
      // keep thumbnails in sync with the selected variant
      if (this.variant && this.variant.image) {
        this.offer.querySelectorAll('[data-kit-main-thumb] img').forEach((img) => {
          img.src = this.variant.image;
          img.removeAttribute('srcset');
        });
      }
      return selectedTotals;
    }

    selectedOfferLabel() {
      const card = this.cards[this.selected];
      if (!card) return '';
      const t = card.querySelector('.kit-offer__title');
      return t ? t.childNodes[0].textContent.trim() : '';
    }

    /* ---------------- Quantity (no offer) ---------------- */
    initQty() {
      this.qtyInput = this.querySelector('[data-kit-qty-input]');
      if (!this.qtyInput) return;
      const set = (n) => {
        this.qtyInput.value = Math.max(1, n || 1);
        this.render();
      };
      this.querySelector('[data-kit-qty-dec]').addEventListener('click', () => set(Number(this.qtyInput.value) - 1));
      this.querySelector('[data-kit-qty-inc]').addEventListener('click', () => set(Number(this.qtyInput.value) + 1));
      this.qtyInput.addEventListener('change', () => set(Number(this.qtyInput.value)));
    }

    qty() {
      return this.qtyInput ? Math.max(1, Number(this.qtyInput.value) || 1) : 1;
    }

    /* ---------------- Render ---------------- */
    render() {
      const v = this.variant;
      const priceNow = this.querySelector('[data-kit-price-now]');
      const priceWas = this.querySelector('[data-kit-price-was]');
      const priceSave = this.querySelector('[data-kit-price-save]');
      const offerTotals = this.renderOffer();
      if (v) {
        // with an offer, the headline price follows the selected pack (same numbers as the ATC button)
        const now = offerTotals ? offerTotals.total : v.price;
        const was = offerTotals ? offerTotals.compare : v.compare;
        if (priceNow) priceNow.textContent = money(now);
        const hasCmp = was > now;
        if (priceWas) {
          priceWas.textContent = hasCmp ? money(was) : '';
          priceWas.hidden = !hasCmp;
        }
        if (priceSave) {
          const pct = hasCmp ? Math.round(((was - now) * 100) / was) : 0;
          priceSave.textContent = `SAVE ${pct}%`;
          priceSave.hidden = pct < 1;
        }
      }

      let total = 0;
      let compare = 0;
      if (offerTotals) {
        total = offerTotals.total;
        compare = offerTotals.compare;
      } else if (v) {
        total = v.price * this.qty();
        compare = Math.max(v.compare || 0, v.price) * this.qty();
      }

      const items = this.buildItems();
      const available = !!v && items.length > 0 && items.every((it) => it.available !== false);
      this.total = total;

      if (this.atc) {
        this.atc.disabled = !available;
        const label = this.atc.querySelector('[data-kit-atc-label]');
        if (label) label.textContent = !v ? 'Unavailable' : available ? `Add to cart · ${money(total)}` : 'Sold out';
      }
      if (this.buyNow) this.buyNow.disabled = !available;

      if (this.sticky) {
        const p = this.sticky.querySelector('[data-kit-sticky-price]');
        const c = this.sticky.querySelector('[data-kit-sticky-compare]');
        const o = this.sticky.querySelector('[data-kit-sticky-offer]');
        const img = this.sticky.querySelector('.kit-sticky__img img');
        const b = this.sticky.querySelector('[data-kit-sticky-atc]');
        if (p) p.textContent = money(total);
        if (c) c.textContent = compare > total ? money(compare) : '';
        if (o) o.textContent = this.offer ? ` · ${this.selectedOfferLabel()}` : '';
        if (img && v && v.image) {
          img.src = v.image;
          img.removeAttribute('srcset');
        }
        if (b) b.disabled = !available;
      }
    }

    /* ---------------- Cart ---------------- */
    buildItems() {
      const v = this.variant;
      if (!v) return [];
      const items = [];
      const push = (variant, quantity, properties) => {
        const existing = !properties && items.find((x) => x.id === variant.id && !x.properties);
        if (existing) existing.quantity += quantity;
        else items.push(Object.assign({ id: variant.id, quantity, available: variant.available }, properties ? { properties } : {}));
      };

      if (this.offer && this.cards.length) {
        const card = this.cards[this.selected];
        const qty = Number(card.dataset.qty || 1);
        if (this.offerType === 'bundle') {
          push(v, 1);
          if (card.hasAttribute('data-bundle')) {
            items.push({ id: Number(this.offer.dataset.bundleVariant), quantity: 1, available: true });
          }
        } else {
          for (let u = 0; u < qty; u++) {
            const uv = this.data.variants.length > 1 ? this.variantById(this.units[u]) || v : v;
            push(uv, 1);
          }
          if (card.dataset.giftVariant) {
            items.push({ id: Number(card.dataset.giftVariant), quantity: 1, available: true, properties: { _kit_gift: '1' } });
          }
        }
      } else {
        push(v, this.qty());
      }
      return items;
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg || '';
      this.errorEl.hidden = !msg;
    }

    async add(buyNow) {
      const items = this.buildItems().map(({ id, quantity, properties }) => (properties ? { id, quantity, properties } : { id, quantity }));
      if (!items.length || !window.Kit) return;
      const btn = buyNow ? this.buyNow : this.atc;
      btn && btn.classList.add('is-loading');
      this.showError('');
      try {
        await window.Kit.addItems(items);
        if (buyNow) {
          window.location.href = '/checkout';
          return;
        }
        window.Kit.afterAdd();
      } catch (err) {
        this.showError(err.message || (window.kitConfig && window.kitConfig.strings.error));
      } finally {
        btn && btn.classList.remove('is-loading');
      }
    }

    initButtons() {
      if (this.atc) this.atc.addEventListener('click', () => this.add(false));
      if (this.buyNow) this.buyNow.addEventListener('click', () => this.add(true));
    }

    /* ---------------- Sticky ATC ---------------- */
    initSticky() {
      this.sticky = this.querySelector('[data-kit-sticky]');
      if (!this.sticky || !this.atc) return;
      document.body.appendChild(this.sticky); // escape any transformed ancestor
      const btn = this.sticky.querySelector('[data-kit-sticky-atc]');
      if (btn) btn.addEventListener('click', () => this.add(false));
      let shown = false;
      let raf = 0;
      const update = () => {
        raf = 0;
        const show = this.atc.getBoundingClientRect().bottom < 0;
        if (show === shown) return;
        shown = show;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (btn) btn.tabIndex = show ? 0 : -1;
        document.documentElement.classList.toggle('kit-has-sticky', show);
      };
      const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    }

    /* ---------------- Delivery estimate ---------------- */
    initDelivery() {
      const el = this.querySelector('[data-kit-delivery]');
      if (!el) return;
      const min = Number(el.dataset.min || 0);
      const max = Number(el.dataset.max || 0);
      if (!min || !max) return;
      const addBusinessDays = (date, days) => {
        const d = new Date(date);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          const day = d.getDay();
          if (day !== 0 && day !== 6) added++;
        }
        return d;
      };
      const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const today = new Date();
      const tpl = el.dataset.template || 'Order today · arrives <b>[from] – [to]</b>';
      el.querySelector('[data-kit-delivery-text]').innerHTML = tpl
        .replace('[from]', fmt(addBusinessDays(today, min)))
        .replace('[to]', fmt(addBusinessDays(today, max)));
      el.hidden = false;
    }
  }

  customElements.define('kit-product', KitProduct);
})();
